import {
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { CreateFormPruffConnect } from 'lib/api/express/sgrWarrantyRequest/types';
import BusinessInput from 'lib/common/BusinessInput';
import { CURRENCIES, SGR_TYPES } from 'lib/consts';
import { isAdmin } from 'lib/functions/utils';
import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';

type Props = {
  form: CreateFormPruffConnect;
  setForm: (form: CreateFormPruffConnect) => void;
};

const CreateWarrantyForm: React.FC<Props> = ({ form, setForm }) => {
  const auth = useSelector((state: RootState) => state.auth);
  const userIsAdmin = auth.user ? isAdmin(auth.user) : false;

  const daysDifference = dayjs(form.endDate).diff(form.startDate, 'day');

  const updateField = (updates: Partial<CreateFormPruffConnect>) => {
    setForm({ ...form, ...updates });
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="ID Licitación"
          value={form.tenderId}
          onChange={(e) => updateField({ tenderId: e.target.value })}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <BusinessInput
          key={form.principalRut}
          identifier={form.principalRut}
          unitCode={form.unitCode}
          onChange={(principalRut, unitCode) =>
            updateField({ principalRut, unitCode })
          }
          required
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          select
          fullWidth
          label="Tipo de Garantía"
          value={form.type}
          onChange={(e) => updateField({ type: e.target.value })}
          required
        >
          {SGR_TYPES.map((type) => (
            <MenuItem key={type.value} value={type.value}>
              {type.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Monto Cotizado"
          type="number"
          value={form.quotedAmount === 0 ? '' : form.quotedAmount}
          onChange={(e) =>
            updateField({ quotedAmount: Number(e.target.value) })
          }
          inputProps={{ step: 'any', min: 0 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {form.quotedCurrency}
              </InputAdornment>
            ),
          }}
          required
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          select
          fullWidth
          label="Moneda"
          value={form.quotedCurrency}
          onChange={(e) => updateField({ quotedCurrency: e.target.value })}
          required
        >
          {CURRENCIES.map((currency) => (
            <MenuItem key={currency.value} value={currency.value}>
              {currency.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} md={6}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
          <DatePicker
            label="Fecha Inicio"
            value={form.startDate}
            onChange={(date) => updateField({ startDate: date })}
            minDate={userIsAdmin ? undefined : dayjs().subtract(7, 'day')}
            maxDate={userIsAdmin ? undefined : dayjs().add(7, 'day')}
            format="DD/MM/YYYY"
            slotProps={{
              textField: { fullWidth: true, required: true },
            }}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} md={6}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
          <DatePicker
            label="Fecha Término"
            value={form.endDate}
            onChange={(date) => updateField({ endDate: date })}
            format="DD/MM/YYYY"
            slotProps={{ textField: { fullWidth: true, required: true } }}
          />
        </LocalizationProvider>
        {daysDifference > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: 'block' }}
          >
            Duración de la garantía: {daysDifference} días
          </Typography>
        )}
      </Grid>
    </Grid>
  );
};

export default CreateWarrantyForm;
